/*!
 * Stockfish.js (http://github.com/nmrugg/stockfish.js)
 * License: GPL
 */

/*
 * Description of the universal chess interface (UCI)  https://gist.github.com/aliostad/f4470274f39d29b788c1b09519e67372/
 */

// Worker is instantiated per-Engine so multiple instances don't share state

type EngineMessage = {
    /** stockfish engine message in UCI format*/
    uciMessage: string;
    /** found best move for current position in format `e2e4`*/
    bestMove?: string;
    /** found best move for opponent in format `e7e5` */
    ponder?: string;
    /**  material balance's difference in centipawns(IMPORTANT! stockfish gives the cp score in terms of whose turn it is)*/
    positionEvaluation?: string;
    /** count of moves until mate */
    possibleMate?: string;
    /** the best line found */
    pv?: string;
    /** number of halfmoves the engine looks ahead */
    depth?: number;
    /** time spent searching in milliseconds */
    time?: number;
    halfMoveNumber: number;
    fen: string;
    lastMove?: string;
};

export default class Engine {
    stockfish: Worker;
    onMessage: (callback: (messageData: EngineMessage) => void) => void;
    isReady: boolean;
    private turn?: "w" | "b";
    private halfMoveNumber: number;
    private fen: string;
    private lastMove?: string;
    private searchQueue: Array<() => void> = [];
    private isSearching = false;

    constructor() {
        this.stockfish = new Worker("./stockfish-17.1-lite-single-03e3232.js");
        this.isReady = false;
        this.turn = undefined;
        this.halfMoveNumber = 0;
        this.fen = "";
        this.onMessage = (callback) => {
            this.stockfish.addEventListener("message", (e) => {
                callback(this.transformSFMessageData(e));
            });
        };
        // Advance the queue when a search completes
        this.stockfish.addEventListener("message", (e) => {
            const uciMessage: string = e?.data ?? e;
            if (typeof uciMessage === "string" && /^bestmove\b/.test(uciMessage)) {
                this.isSearching = false;
                this.runNext();
            }
        });
        this.init();
    }

    private runNext() {
        if (this.searchQueue.length === 0) {
            this.isSearching = false;
            return;
        }
        this.isSearching = true;
        this.searchQueue.shift()!();
    }

    private transformSFMessageData(e) {
        const uciMessage = e?.data ?? e;

        let positionEvaluation = uciMessage.match(/cp\s+(\S+)/)?.[1];

        // If we have a position evaluation and current FEN, check if it's black to move
        if (positionEvaluation && this.turn) {
            try {
                if (this.turn === "b") {
                    // Flip the evaluation for black to move
                    positionEvaluation = -parseInt(positionEvaluation);
                }
            } catch (error) {
                // If FEN parsing fails, keep original evaluation
                console.warn("Failed to parse FEN for evaluation flipping:", error);
            }
        }
        positionEvaluation = (parseInt(positionEvaluation) / 100).toFixed(1);

        // mate score
        const mateMatch = uciMessage.match(/\bmate\s+(-?\d+)/);
        if (mateMatch) {
            const mate = parseInt(mateMatch[1]);
            let bestScoreNumber = mate > 0 ? 100 : -100;
            if (this.turn === "b") {
                bestScoreNumber = -bestScoreNumber;
            }

            if (mate === 0) {
                if (this.turn === "w") {
                    bestScoreNumber = -100;
                } else {
                    bestScoreNumber = 100;
                }
            }
            positionEvaluation = bestScoreNumber.toString();
        }

        return {
            uciMessage,
            bestMove: uciMessage.match(/bestmove\s+(\S+)/)?.[1],
            ponder: uciMessage.match(/ponder\s+(\S+)/)?.[1],
            positionEvaluation,
            possibleMate: uciMessage.match(/mate\s+(\S+)/)?.[1],
            pv: uciMessage.match(/ pv\s+(.*)/)?.[1],
            depth: Number(uciMessage.match(/ depth\s+(\S+)/)?.[1]) ?? 0,
            time:
                uciMessage.match(/\btime\s+(\d+)/)?.[1] !== undefined
                    ? Number(uciMessage.match(/\btime\s+(\d+)/)?.[1])
                    : undefined,
            halfMoveNumber: this.halfMoveNumber,
            fen: this.fen,
            lastMove: this.lastMove,
        };
    }

    init() {
        this.stockfish.postMessage("uci");
        this.stockfish.postMessage("isready");
        this.onMessage(({ uciMessage }) => {
            if (uciMessage === "readyok") {
                this.isReady = true;
            }
        });
    }

    onReady(callback) {
        this.onMessage(({ uciMessage }) => {
            if (uciMessage === "readyok") {
                callback();
            }
        });
    }

    evaluatePosition(
        initialFen: string,
        fen: string,
        searchMovesString: string,
        depth: "Auto" | number,
        turn: "w" | "b" = "w",
        halfMoveNumber: number,
        lastMove?: string,
    ) {
        const task = () => {
            this.turn = turn;
            this.halfMoveNumber = halfMoveNumber;
            this.fen = fen;
            this.lastMove = lastMove;

            this.stockfish.postMessage(`position fen ${initialFen}${searchMovesString}`);
            // this.stockfish.postMessage(`go depth ${depth}`);

            if (depth === "Auto") {
                this.stockfish.postMessage(`go movetime 500`);
            } else {
                this.stockfish.postMessage(`go depth ${depth}`);
            }
        };

        this.searchQueue.push(task);
        if (!this.isSearching) {
            this.runNext();
        }
    }

    /**
     * Evaluates a FEN position using local Stockfish and resolves with the result.
     * Score is always from white's perspective (positive = white is better).
     */
    evaluatePositionAsync(
        fen: string,
        depth: "Auto" | number = "Auto",
    ): Promise<{ score: string; bestMove: string | undefined; depth: number; time: number }> {
        return new Promise((resolve) => {
            // Determine whose turn it is from the FEN
            const turn = (fen.split(" ")[1] ?? "w") as "w" | "b";
            this.turn = turn;

            let bestScore = "0.0";
            let time = 0;
            let evaluationDepth = 0;

            const messageHandler = (e: MessageEvent) => {
                const uciMessage: string = e?.data ?? e;

                // cp score is from the side-to-move's perspective in UCI
                const cpMatch = uciMessage.match(/\bcp\s+(-?\d+)/);
                if (cpMatch) {
                    let cp = parseInt(cpMatch[1]);
                    // Convert to white's perspective
                    if (turn === "b") cp = -cp;
                    bestScore = (cp / 100).toFixed(1);

                    time =
                        uciMessage.match(/\btime\s+(\d+)/)?.[1] !== undefined
                            ? Number(uciMessage.match(/\btime\s+(\d+)/)?.[1])
                            : 0;
                    evaluationDepth = Number(uciMessage.match(/ depth\s+(\S+)/)?.[1]) ?? 0;
                }

                // mate score
                const mateMatch = uciMessage.match(/\bmate\s+(-?\d+)/);
                if (mateMatch) {
                    const mate = parseInt(mateMatch[1]);
                    let bestScoreNumber = mate > 0 ? 100 : -100;
                    if (this.turn === "b") {
                        bestScoreNumber = -bestScoreNumber;
                    }

                    if (mate === 0) {
                        if (this.turn === "w") {
                            bestScoreNumber = -100;
                        } else {
                            bestScoreNumber = 100;
                        }
                    }
                    bestScore = bestScoreNumber.toString();
                }

                // bestmove signals the end of the search
                const bestMoveMatch = uciMessage.match(/bestmove\s+(\S+)/);
                if (bestMoveMatch) {
                    const bestMove = bestMoveMatch[1] === "(none)" ? undefined : bestMoveMatch[1];
                    this.stockfish.removeEventListener("message", messageHandler);
                    resolve({ score: bestScore, bestMove, depth: evaluationDepth, time });
                }
            };

            // Stop any ongoing search, then start the new one
            this.stockfish.postMessage("stop");
            this.stockfish.addEventListener("message", messageHandler);
            this.stockfish.postMessage(`position fen ${fen}`);
            // this.stockfish.postMessage(`go depth ${depth}`);

            if (depth === "Auto") {
                this.stockfish.postMessage(`go movetime 500`);
            } else {
                this.stockfish.postMessage(`go depth ${depth}`);
            }
        });
    }

    stop() {
        this.stockfish.postMessage("stop"); // Run when searching takes too long time and stockfish will return you the bestmove of the deep it has reached
    }

    terminate() {
        this.isReady = false;
        this.stockfish.postMessage("quit"); // Run this before chessboard unmounting.
    }
}
